import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import { getLocales } from 'assets/locales/Locale';
import { replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { productManagementHomeRoute } from 'config/Api.Config';
import { isMobile } from 'react-device-detect';
import {
  ProductEnhancementDocumentTabs,
  useProductEnhancementDocumentProvider,
} from '../ProductEnhancementDocument.Hooks';
import {
  ProductEnhancementDealerDocumentsAction,
  useProductEnhancementDealerDocumentProvider,
} from './ProductEnhancementDocument.DealerDocuments.Hooks';
import ProductDocumentsDealerFilters from './sections/ProductEnhancementDocument.DealerDocuments.Filters';
import DealerDocumentList from './sections/ProductEnhancementDocument.DealerDocuments.List';
import ProductEnhancementDealerDocumentAdd from './sections/ProductEnhancementDocument.DealerDocuments.Add';
import Theme from 'assets/themes/Theme.Common';
import ThemeButtonTab from 'assets/components/button/themes/Theme.Button.Tab';
import UploadDocumentTranslationButton from '../../../components/translations/UploadDocumentTranslationButton';
import Box from 'assets/components/box/Box';

enum Actions {
  ADD = 'add',
  DELETE = 'delete',
}
export type Verify = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Actions,
  Utils.ValueOf<typeof Actions>
>;
export const productDocumentIndexRoute = productManagementHomeRoute + '/productDocument/:id?';
export default function ProductEnhancementDealerDocumentIndex() {
  const { lang } = getLocales();
  const productEnhancementDocumentHook = useProductEnhancementDocumentProvider();
  const {
    currentView,
    setView,
    views,
    selectedItems,
    toggleSelectAll,
    dealerDocumentList,
    handleDeleteDealerDocumentSubmit,
    translatedDocumentList,
    selectedTranslatedItems,
    toggleSelectAllTranslations,
  } = useProductEnhancementDealerDocumentProvider();

  return (
    <Page
      class={ThemePageList}
      isLoading={productEnhancementDocumentHook.isLoadingProductEnhancementDocument}
      style={{
        pageHeader: {
          flexDirection: 'column',
          alignItems: 'flex-start',
          paddingBottom: 0,
          background: Theme.Color.primary,
          color: Theme.Color.primaryAlt,
          borderBottom: 'none',
        },
      }}
      header={
        <>
          <Box style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
            <Card
              class={ThemeCardRow}
              style={{
                cardMedia: {
                  color: Theme.Color.primary,
                  background: Theme.Color.primaryAlt,
                },
              }}
              media={<Icon class="fas-file-contract" />}
              info={lang.productDocuments}
              title={productEnhancementDocumentHook.productEnhancementDocument?.displayInfo?.title}
              subtitle={productEnhancementDocumentHook.productEnhancementDocument?.documentCategory}
            />
            {productEnhancementDocumentHook.productEnhancementDocument && (
              <Box>
                <UploadDocumentTranslationButton
                  documentId={productEnhancementDocumentHook.productEnhancementDocument.id}
                  documentType="ProductEnhancementDocument"
                  translatedDocuments={productEnhancementDocumentHook.translatedDocumentList}
                  onFinish={productEnhancementDocumentHook.reloadTranslatedDocs}
                />
              </Box>
            )}
          </Box>
          <div
            style={{
              height: 'fit-content',
              fontSize: Theme.FontSize.M,
              display: 'flex',
              marginLeft: Theme.Size.pixel(10),
            }}
            children={
              <>
                <Button
                  key={'dealers-tab'}
                  render={lang.dealers}
                  onClick={productEnhancementDocumentHook.setSelectedTab}
                  data={ProductEnhancementDocumentTabs.DEALERS}
                  active={productEnhancementDocumentHook.selectedTab === ProductEnhancementDocumentTabs.DEALERS}
                  class={ThemeButtonTab}
                />
                <Button
                  key={'dealer-groups-tab'}
                  render={lang.dealerGroup}
                  onClick={productEnhancementDocumentHook.setSelectedTab}
                  data={ProductEnhancementDocumentTabs.DEALER_GROUPS}
                  active={productEnhancementDocumentHook.selectedTab === ProductEnhancementDocumentTabs.DEALER_GROUPS}
                  class={ThemeButtonTab}
                />
              </>
            }
          />
        </>
      }
      subHeader={
        <>
          <b style={{ marginRight: 'auto' }} children={lang.productEnhancementDocuments} />
          <Button
            class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
            active
            media={<Icon class="fas-plus" />}
            render={!isMobile && lang.add}
            onClick={setView}
            data={ProductEnhancementDealerDocumentsAction.ADD}
          />
          &nbsp;
          {selectedItems?.length || selectedTranslatedItems?.length ? (
            <>
              <Button
                class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
                active
                media={<Icon class="fas-trash" />}
                render={!isMobile && lang.delete}
                onClick={setView}
                data={ProductEnhancementDealerDocumentsAction.DELETE}
              />
              {currentView === ProductEnhancementDealerDocumentsAction.DELETE && (
                <Confirm
                  onConfirm={() => {
                    setView(undefined);
                    handleDeleteDealerDocumentSubmit();
                  }}
                  onClose={() => setView(undefined)}
                  actionName={replaceStringTemplateWithValues(lang.productDealerDocumentDeleteSubmitMessage, {
                    numberOfDocuments: (selectedItems.length ?? 0).toString(),
                  })}
                  confirmationType="nextButton"
                  subcomponents={ThemeConfirmDefault}
                />
              )}
            </>
          ) : null}
          &nbsp;
          {dealerDocumentList.count > 0 && (
            <>
              <Button
                class={{ button: ThemeButtonCircle }}
                media={
                  selectedItems?.length === dealerDocumentList.count
                    ? 'fas-check-square'
                    : selectedItems?.length !== dealerDocumentList.count
                      ? 'fas-minus-square'
                      : 'far-square'
                }
                data={!(selectedItems?.length === dealerDocumentList.count)}
                onClick={toggleSelectAll}
                active={selectedItems?.length === dealerDocumentList.count}
                htmlElementProps={{ title: `${lang.selectAll} (EN)` }}
                render={`${lang.selectAll} (EN)`}
              />
              &nbsp;
            </>
          )}
          {translatedDocumentList.length > 0 && (
            <>
              <Button
                class={{ button: ThemeButtonCircle }}
                media={
                  selectedTranslatedItems?.length === translatedDocumentList.length
                    ? 'fas-check-square'
                    : selectedTranslatedItems?.length !== translatedDocumentList.length
                      ? 'fas-minus-square'
                      : 'far-square'
                }
                data={!(selectedTranslatedItems?.length === translatedDocumentList.length)}
                onClick={toggleSelectAllTranslations}
                active={selectedTranslatedItems?.length === translatedDocumentList.length}
                htmlElementProps={{ title: `${lang.selectAll} (FR)` }}
                render={`${lang.selectAll} (FR)`}
              />
              &nbsp;
            </>
          )}
          <ProductDocumentsDealerFilters />
        </>
      }
      render={
        <>
          <DealerDocumentList />
          {views.add && <ProductEnhancementDealerDocumentAdd />}
        </>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={dealerDocumentList.count}
          offset={dealerDocumentList.offset}
          limit={dealerDocumentList.limit}
          onLimitChange={(limit) =>
            dealerDocumentList.setDealerDocumentFilters((old) => ({
              ...old,
              limit,
              offset: 0,
            }))
          }
          onOffsetChange={(offset) =>
            dealerDocumentList.setDealerDocumentFilters((old) => ({
              ...old,
              offset,
            }))
          }
        />
      }
    />
  );
}
